/**
 * WKS Varianten-Merker 3.5 – Prestige / Shopify
 * Produktdetailseiten: Größe und Ausführungsgruppe merken.
 * Produktkarten bleiben unberührt.
 * Zirbelholz-Erkennung über Liquid-Markierung data-wks-zirbel-product.
 */
(() => {
  'use strict';
  if (window.WKSVariantMemory?.version === '3.5') return;
  const VERSION = '3.5';
  const KEY = 'wks:variant-memory';
  const LEGACY = 'wks:variant-memory:';
  const COLORS = ['bemalt', 'color', 'lasiert', 'wasserfarben bemalt', 'lasiert wasserfarben'];
  const EXACT = ['mehrfach gebeizt'];
  const norm = v => String(v ?? '').trim().toLocaleLowerCase('de-DE').replace(/\s+/g, ' ');
  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '{}');
      return {
        größe: typeof v.größe === 'string' ? v.größe : '',
        ausführung: ['natur', 'farbig', ...EXACT].includes(norm(v.ausführung)) ? norm(v.ausführung) : ''
      };
    } catch { return { größe: '', ausführung: '' }; }
  };
  const write = value => {
    try { localStorage.setItem(KEY, JSON.stringify(value)); } catch { /* private mode */ }
  };
  const cleanup = () => {
    try {
      for (const key of Object.keys(localStorage)) if (key.startsWith(LEGACY) && key !== KEY) localStorage.removeItem(key);
    } catch { /* private mode */ }
  };
  const config = document.querySelector('[data-wks-variant-memory]');
  if (!config) return;
  const formId = config.dataset.wksFormId || '';
  const escape = v => window.CSS?.escape ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
  const picker = () => {
    if (!formId) return null;
    return document.querySelector(`variant-picker[form-id="${escape(formId)}"]`) ||
      document.querySelector(`variant-picker[form="${escape(formId)}"]`);
  };
  const zirbel = () => document.querySelector('[data-wks-zirbel-product="true"]') !== null;
  const groups = () => {
    const p = picker();
    if (!p) return {};
    const result = {};
    for (const field of p.querySelectorAll('fieldset')) {
      const legend = norm(field.querySelector('legend')?.textContent || '');
      const inputName = norm(field.querySelector('input[type="radio"]')?.name || '');
      if (legend.includes('größe') || inputName.includes('größe')) result.size = field;
      if (legend.includes('ausführung') || inputName.includes('ausführung')) result.execution = field;
    }
    return result;
  };
  const labelText = input => {
    const label = input.labels?.[0] || (input.id ? document.querySelector(`label[for="${escape(input.id)}"]`) : null) || input.closest('label');
    if (label) {
      for (const selector of ['[data-option-value]', '.variant-picker__option-value', 'span:first-child']) {
        const found = label.querySelector(selector);
        if (found && norm(found.textContent) && !/nicht verfügbar|ausverkauft/.test(norm(found.textContent))) return norm(found.textContent);
      }
      const copy = label.cloneNode(true);
      copy.querySelectorAll('[class*="sold"], [class*="unavailable"]').forEach(el => el.remove());
      const text = norm(copy.textContent).replace(/nicht verfügbar|ausverkauft/g, '').trim();
      if (text) return text;
    }
    return norm(input.dataset.optionValue || input.dataset.value || input.getAttribute('aria-label') || input.value);
  };
  const available = input => {
    if (input.disabled || input.getAttribute('aria-disabled') === 'true') return false;
    const label = input.labels?.[0] || (input.id ? document.querySelector(`label[for="${escape(input.id)}"]`) : null);
    return !label || (!/nicht verfügbar|ausverkauft/.test(norm(label.textContent)) && !label.matches('[aria-disabled="true"], .disabled, [class*="disabled"]'));
  };
  const radio = (field, value) => field && [...field.querySelectorAll('input[type="radio"]')]
    .find(input => available(input) && labelText(input) === norm(value));
  const preferred = (field, values) => values.map(v => radio(field, v)).find(Boolean);
  const selected = field => field?.querySelector('input[type="radio"]:checked');
  const category = value => {
    const v = norm(value);
    return v === 'natur' ? 'natur' : COLORS.includes(v) ? 'farbig' : EXACT.includes(v) ? v : '';
  };
  let applying = false;
  let userInteracted = false;
  let lastUserChange = 0;
  let applyTimer;
  let saveTimer;
  let attempts = 0;
  let completed = false;
  const maxAttempts = 24;
  const start = Date.now();
  const targetExecution = memory => {
    if (memory.ausführung === 'natur') return ['natur', ...COLORS];
    if (memory.ausführung === 'farbig') return [...COLORS, 'natur'];
    if (EXACT.includes(memory.ausführung)) return [memory.ausführung, ...COLORS, 'natur'];
    return zirbel() ? ['natur', ...COLORS] : [...COLORS, 'natur'];
  };
  const choose = input => {
    if (!input || input.checked) return false;
    applying = true;
    try { input.click(); } finally {
      setTimeout(() => { applying = false; }, 500);
    }
    return true;
  };
  const apply = () => {
    if (userInteracted || Date.now() - lastUserChange < 1500) return;
    const g = groups();
    if (!picker() || (!g.size && !g.execution)) return;
    const memory = read();
    const sizeTarget = memory.größe ? radio(g.size, memory.größe) : null;
    if (sizeTarget && !sizeTarget.checked) {
      choose(sizeTarget);
      // Prestige kann den Picker nach Größenwechsel ersetzen.
      scheduleApply(160);
      return;
    }
    const executionTarget = preferred(g.execution, targetExecution(memory));
    if (executionTarget && !executionTarget.checked) {
      choose(executionTarget);
      scheduleApply(180);
      return;
    }
    // Erst als abgeschlossen ansehen, wenn die gewünschte Auswahl im DOM steht.
    if ((!memory.größe || !sizeTarget || sizeTarget.checked) && (!executionTarget || executionTarget.checked)) completed = true;
  };
  const scheduleApply = (delay = 100) => {
    if (userInteracted || attempts >= maxAttempts || Date.now() - start > 6500) return;
    clearTimeout(applyTimer);
    applyTimer = setTimeout(() => { attempts++; apply(); }, delay);
  };
  const save = () => {
    const g = groups();
    const memory = read();
    const size = selected(g.size);
    const execution = selected(g.execution);
    if (size) memory.größe = labelText(size);
    if (execution) {
      const c = category(labelText(execution));
      if (c) memory.ausführung = c;
    }
    write(memory);
  };
  const onUserInput = event => {
    const p = picker();
    if (p && p.contains(event.target)) {
      userInteracted = true;
      clearTimeout(applyTimer);
    }
  };
  // Nur echte Interaktion darf den Speicher ändern; programmatische Auswahl nicht.
  document.addEventListener('pointerdown', onUserInput, true);
  document.addEventListener('keydown', event => {
    if (['Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) onUserInput(event);
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches?.('input[type="radio"]')) return;
    const p = picker();
    if (!p || !p.contains(event.target) || applying || !userInteracted) return;
    lastUserChange = Date.now();
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 180);
  }, true);
  // Vor Verlassen der Seite eine noch ausstehende Kundenauswahl sichern.
  window.addEventListener('pagehide', () => {
    if (userInteracted && !applying) { clearTimeout(saveTimer); save(); }
  });
  cleanup();
  const observer = new MutationObserver(() => {
    if (!userInteracted && !completed && !applying) scheduleApply(120);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 7000);
  requestAnimationFrame(() => scheduleApply(0));
  [350, 850, 1600, 2800].forEach(delay => setTimeout(() => {
    if (!userInteracted && !completed) scheduleApply(0);
  }, delay));
  window.WKSVariantMemory = {
    version: VERSION,
    getMemory: read,
    reset() { try { localStorage.removeItem(KEY); cleanup(); } catch { /* private mode */ } },
    apply() { completed = false; userInteracted = false; attempts = 0; apply(); },
    debug() { return { memory: read(), zirbel: zirbel(), picker: Boolean(picker()), completed, userInteracted, attempts }; }
  };
})();
